import {
    ActionType,
    AddVSRLookupAction,
    ClearedStedsnavnAction,
    ErrorAction,
    FailedOmradeGeometryAction,
    GetState,
    ReceivedOmradeGeometryAction,
    ReceivedStedsnavnAction,
    UpdateVSRAction,
} from '@/actions/actiontypes';
import { zoomToExtent } from '@/actions/mapActions';
import { handleError } from '@/actions/metaActions';
import { HistoricalRoadReference } from '@/domain/HistoricalRoadReference';
import { LegacyVegreferanse } from '@/domain/LegacyVegreferanse';
import { Omrade, validateVegsystemreferanse, Vegsystemreferanse } from '@/domain/omrader';
import { Vegobjekt, vegobjektVersionsComparator } from '@/domain/vegobjekter/Vegobjekt';
import { Veglenkesekvensposisjon } from '@/domain/Veglenkesekvensposisjon';
import { WKTObject } from '@/domain/WKTObject';
import { StedsnavnResponse } from '@/server/responses';
import { isRequestAborted } from '@/server/server';
import { RootState, Severity, VegkartError, VegsystemreferanseLookup } from '@/state';
import { Dispatch } from '@/state/store';
import { createLogger } from '@/utils/Logger';
import { kartutsnittFromWktStrings } from '@/utils/SpatialHelper';
import * as selectors from '../selectors/selectors';
import { Integration } from './integration';
import { geometryTolerance } from '@/middleware/fetchHelpers';

const log = createLogger('fetchdata');
let activeLegacyRoadReferenceLookupController: AbortController | null = null;

function beginLegacyRoadReferenceLookup(): AbortController {
    activeLegacyRoadReferenceLookupController?.abort();
    const controller = new AbortController();
    activeLegacyRoadReferenceLookupController = controller;
    return controller;
}

function finishLegacyRoadReferenceLookup(controller: AbortController): void {
    if (activeLegacyRoadReferenceLookupController === controller) {
        activeLegacyRoadReferenceLookupController = null;
    }
}

function isVegsystemreferanseLookup(
    value: VegsystemreferanseLookup | VegkartError
): value is VegsystemreferanseLookup {
    return value instanceof VegsystemreferanseLookup;
}

function clearVegsystemreferanseGeometry(
    vegsystemreferanse: Vegsystemreferanse
): Vegsystemreferanse {
    return Object.assign(vegsystemreferanse.override({ geometri: null }), {
        senterpunkt: null,
    });
}

function zoomToVsrLookupGeometry(
    dispatch: Dispatch,
    vsrLookup: VegsystemreferanseLookup
): void {
    const geometri = vsrLookup.vegsystemreferanse?.geometri
        ? vsrLookup.vegsystemreferanse.geometri
        : vsrLookup.geometri;

    if (geometri?.wkt) {
        dispatch(zoomToExtent(kartutsnittFromWktStrings(geometri.wkt)));
    }
}

export const fetchPlaceNames =
    (searchString: string, page: number = 1) =>
    (dispatch: Dispatch, getState: GetState, integration: Integration) => {
        log('fetchPlaceNames {}', searchString);
        const append = page > 1;
        integration.server
            .getStedsnavn(searchString, page)
            .then((stedsnavn: StedsnavnResponse) => {
                const stedsnavnAction: ReceivedStedsnavnAction = {
                    stedsnavn,
                    type: ActionType.SEARCH_STEDSNAVN_RECEIVED,
                    ...(append && { append }),
                };
                dispatch(stedsnavnAction);
            });
    };

export const clearPlaceNames = () => (dispatch: Dispatch) => {
    const action: ClearedStedsnavnAction = { type: ActionType.SEARCH_STEDSNAVN_CLEARED };
    dispatch(action);
};

export const lookupVSR =
    (coordinate: [number, number], shouldZoom: boolean = false) =>
    (dispatch: Dispatch, getState: GetState, integration: Integration) => {
        const timestamp = selectors.stateSearchTimestamp(getState());

        integration.server
            .getVSRFromCoordinate(coordinate, timestamp ?? undefined)
            .then(vsrLookup => {
                if (shouldZoom) zoomToVsrLookupGeometry(dispatch, vsrLookup);
                const addVSRLookupAction: AddVSRLookupAction = {
                    type: ActionType.OVERLAY_VEGSYSTEMREFERANSE_ADDED,
                    vsrLookup,
                };
                dispatch(addVSRLookupAction);
            })
            .catch(rejection => {
                const error = new VegkartError(
                    rejection === 404
                        ? 'Finner ingen vegsystemreferanser i nærheten'
                        : 'Feil ved vegsystemreferanse-oppslag.',
                    { response: rejection.response },
                    rejection === 404 ? Severity.INFO : Severity.ERROR
                );
                const errorAction: ErrorAction = {
                    type: ActionType.ERROR_OCCURRED,
                    error,
                };
                dispatch(errorAction);
            });
    };

export const loopupCoordinateForRoadref =
    (roadsysref: Vegsystemreferanse) =>
    (dispatch: Dispatch, getState: GetState, integration: Integration) => {
        const roadsysrefForLookup = roadsysref.kortform.replace(/-\d+/, '');

        integration.server
            .getCoordinateFromVegsystemreferanse(roadsysrefForLookup)
            .then(vsrLookup => {
                if (!isVegsystemreferanseLookup(vsrLookup)) throw vsrLookup;
                zoomToVsrLookupGeometry(dispatch, vsrLookup);
                const addVSRAction: AddVSRLookupAction = {
                    type: ActionType.OVERLAY_VEGSYSTEMREFERANSE_ADDED,
                    vsrLookup,
                };
                dispatch(addVSRAction);
            })
            .catch(rejection => {
                if (rejection !== 404) {
                    const error = new VegkartError(
                        'Feil ved vegsystemreferanse-oppslag.',
                        { response: rejection.response },
                        Severity.ERROR
                    );
                    const errorAction: ErrorAction = {
                        type: ActionType.ERROR_OCCURRED,
                        error,
                    };
                    dispatch(errorAction);
                }
            });
    };

export const lookupVSRForVeglenkesekvens =
    (veglenkesekvens: string, shouldZoom: boolean = false) =>
    (dispatch: Dispatch, getState: GetState, integration: Integration) => {
        integration.server
            .getVSRFromVeglenkesekvens(veglenkesekvens)
            .then(vsrLookup => {
                if (!isVegsystemreferanseLookup(vsrLookup)) throw vsrLookup;
                if (shouldZoom) zoomToVsrLookupGeometry(dispatch, vsrLookup);
                const addVSRAction: AddVSRLookupAction = {
                    type: ActionType.OVERLAY_VEGSYSTEMREFERANSE_ADDED,
                    vsrLookup,
                };
                dispatch(addVSRAction);
            })
            .catch(rejection => {
                const error = new VegkartError(
                    rejection === 404
                        ? 'Finner ingen vegsystemreferanser for veglenkeposisjonen'
                        : 'Feil ved vegsystemreferanse-oppslag.',
                    { response: rejection.response },
                    rejection === 404 ? Severity.INFO : Severity.ERROR
                );
                const errorAction: ErrorAction = {
                    type: ActionType.ERROR_OCCURRED,
                    error,
                };
                dispatch(errorAction);
            });
    };

export const fetchHistoricalRoadReferences =
    (veglenke: Veglenkesekvensposisjon) =>
    async (
        dispatch: Dispatch,
        getState: GetState,
        integration: Integration
    ): Promise<HistoricalRoadReference[]> => {
        const timestamp = selectors.stateSearchTimestamp(getState());
        return integration.server.getHistoricalRoadReferences(
            veglenke,
            timestamp ?? undefined
        );
    };

export const lookupVSRForLegacyVegreferanse =
    (vegreferanse: LegacyVegreferanse) =>
    async (
        dispatch: Dispatch,
        getState: GetState,
        integration: Integration
    ): Promise<void> => {
        const timestamp = selectors.stateSearchTimestamp(getState());
        const controller = beginLegacyRoadReferenceLookup();
        try {
            const references =
                await integration.server.getHistoricalRoadReferencesFromLegacyVegreferanse(
                    vegreferanse,
                    timestamp ?? undefined,
                    controller.signal
                );
            const firstReference = references[0];
            if (!firstReference) throw 404;

            const vsrLookup = await integration.server.getVSRFromVeglenkesekvens(
                firstReference.roadLinkPosition,
                controller.signal
            );
            if (!isVegsystemreferanseLookup(vsrLookup)) throw vsrLookup;
            zoomToVsrLookupGeometry(dispatch, vsrLookup);
            const addVSRAction: AddVSRLookupAction = {
                type: ActionType.OVERLAY_VEGSYSTEMREFERANSE_ADDED,
                vsrLookup,
            };
            dispatch(addVSRAction);
        } catch (rejection) {
            if (isRequestAborted(rejection)) return;
            const error = new VegkartError(
                rejection === 404
                    ? 'Finner ingen vegsystemreferanser for historisk vegreferanse'
                    : 'Feil ved historisk vegreferanse-oppslag.',
                {
                    response:
                        rejection !== null &&
                        typeof rejection === 'object' &&
                        'response' in rejection
                            ? rejection.response
                            : undefined,
                },
                rejection === 404 ? Severity.INFO : Severity.ERROR
            );
            const errorAction: ErrorAction = { type: ActionType.ERROR_OCCURRED, error };
            dispatch(errorAction);
        } finally {
            finishLegacyRoadReferenceLookup(controller);
        }
    };

export const fetchAllOmradeGeometries = () => (dispatch: Dispatch, state: RootState) => {
    const omraderInSearch = selectors.stateOmrader(state).getGeometryFetchables();

    omraderInSearch.forEach(omrade => {
        dispatch(fetchAreaGeometry(omrade, false));
    });
};

export async function fetchVegSystemReferanse(
    vegsystemreferanse: Vegsystemreferanse,
    integration: Integration
): Promise<Vegsystemreferanse> {
    let wkt: WKTObject;
    const { meterAvgrensning } = vegsystemreferanse;
    if (meterAvgrensning?.startEllerPunkt != null && meterAvgrensning?.slutt != null) {
        wkt = await integration.server.getGeometryOnVegnettsegment(vegsystemreferanse);
    } else if (
        meterAvgrensning?.startEllerPunkt != null &&
        meterAvgrensning?.slutt == null
    ) {
        wkt = await integration.server.getVeg(vegsystemreferanse);
    } else {
        wkt = await integration.server.getGeometryOnStrekning(vegsystemreferanse);
    }
    return vegsystemreferanse.withSenterpunkt(wkt).withGeometri(wkt);
}

export const fetchVegsystemreferansePoint =
    (vegsystemreferanse: Vegsystemreferanse) =>
    async (dispatch: Dispatch, _getState: () => RootState, integration: Integration) => {
        if (!validateVegsystemreferanse(vegsystemreferanse)) {
            let message =
                'Zoom til vegsystemreferanse krever vegnummer, strekning og delstrekning.';
            if (vegsystemreferanse.isKPS() && !vegsystemreferanse.kommune)
                message = 'Zoom til vegsystemreferanse krever kommune';
            const vegkartError = new VegkartError(message, {}, Severity.INFO);
            const errorAction: ErrorAction = {
                type: ActionType.ERROR_OCCURRED,
                error: vegkartError,
            };
            dispatch(errorAction);
        } else {
            try {
                const newVsr = await fetchVegSystemReferanse(
                    vegsystemreferanse,
                    integration
                );
                const action: UpdateVSRAction = {
                    type: ActionType.OMRADE_VEGSYSTEMREFERANSE_UPDATED,
                    previousVsr: vegsystemreferanse,
                    newVsr,
                };
                dispatch(action);
            } catch (err) {
                console.warn(err);
                const message =
                    err === 404
                        ? `Fant ikke vegsystemreferanse`
                        : `Kunne ikke hente vegsystemreferanse`;
                const severity = err === 404 ? Severity.INFO : Severity.ERROR;
                dispatch(
                    handleError(
                        new VegkartError(
                            `${message} ${vegsystemreferanse.kortform}`,
                            { err, vegsystemreferanse: vegsystemreferanse },
                            severity
                        )
                    )
                );
                const updateAction: UpdateVSRAction = {
                    type: ActionType.OMRADE_VEGSYSTEMREFERANSE_UPDATED,
                    previousVsr: vegsystemreferanse,
                    newVsr: clearVegsystemreferanseGeometry(vegsystemreferanse),
                };
                dispatch(updateAction);
            }
        }
    };

export const fetchAreaGeometry =
    (omrade: Omrade, zoom = true) =>
    async (
        dispatch: Dispatch,
        getState: GetState,
        integration: Integration
    ): Promise<boolean> => {
        const timestamp = getState().searchState.searchDropdownState.timestamp;
        const isOmrade = omrade.vegobjekt?.metadata.type.id != null;
        const source = isOmrade
            ? omrade
            : integration.data.kommuner.find(kom => kom.nummer == omrade.nummer);

        const clearLoading = (failedOmrade: Omrade) => {
            const failedAction: FailedOmradeGeometryAction = {
                type: ActionType.OMRADE_GEOMETRI_FETCH_FAILED,
                omrade: failedOmrade,
            };
            dispatch(failedAction);
        };

        if (!source?.vegobjekt) {
            clearLoading(omrade);
            return false;
        }
        const sourceVegobjekt = source.vegobjekt;

        let version: number | undefined;
        try {
            if (timestamp) {
                const vegobjekterFromServer: Vegobjekt[] =
                    await integration.server.getVegobjektVersionsMinimal(
                        sourceVegobjekt.id,
                        sourceVegobjekt.metadata.type.id
                    );
                version = vegobjekterFromServer
                    .filter(v => new Date(v.metadata.startdato) <= timestamp)
                    .sort(vegobjektVersionsComparator)
                    .pop()?.metadata.version;
            } else {
                version = undefined;
            }

            const vegobjektFromServer = await integration.server.getVegobjekt(
                sourceVegobjekt.id,
                undefined,
                sourceVegobjekt.metadata.type.id,
                version
            );

            const receivedOmradeGeometryAction: ReceivedOmradeGeometryAction = {
                type: ActionType.OMRADE_GEOMETRI_RECEIVED,
                vegobjekt: vegobjektFromServer,
                omrade: source,
            };
            dispatch(receivedOmradeGeometryAction);

            const geometryWkts = getState()
                .omraderState.getGeometryFetchables()
                .map(omrade => omrade.vegobjekt?.geometri?.wkt)
                .filter((wkt): wkt is string => Boolean(wkt));

            if (!zoom || geometryWkts.length === 0) {
                return false;
            }

            dispatch(zoomToExtent(kartutsnittFromWktStrings(geometryWkts)));
            return true;
        } catch (err) {
            clearLoading(source);
            dispatch(
                handleError(
                    new VegkartError('Feil ved henting av områdegeometri', {
                        err,
                        area: source,
                    })
                )
            );
            return false;
        }
    };
